<template>
  <div class="search-result-page-wrapper">
    <Cover :lv1_info="searchCoverInfo" :lv2_info="searchCoverLv2Info" class="w-100" />
    <div class="padded-container w-100 search-content-container">
      <b-row class="justify-content-center justify-content-lg-between pt-5">
        <b-col class="d-none d-lg-block invisible" cols="0" lg="1" xl="2" />
        <b-col id="content" cols="12" lg="10" xl="8">
          <SearchResult
            :search-keyword="searchKeyword"
            :search-results="searchResults"
            :loading="loading"
            :lang="lang"
            :main-color="selected_report?.main_color || '#05adc8'"
            :sub-color="selected_report?.sub_color || '#05adc8'"
          />
        </b-col>
        <b-col class="d-none d-lg-block invisible" cols="0" lg="1" xl="2" />
      </b-row>
    </div>
    <custom-footer />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import SearchResult from '@/components/search_bar/SearchResult.vue';
import Cover from '../report_preview/story_board/viewer_component/Cover.vue';
import customFooter from '../report_preview/story_board/footer/custom-footer.vue';

export default {
  name: 'SearchResultPage',
  components: {
    SearchResult,
    Cover,
    customFooter
  },
  data() {
    return {
      searchResults: [],
      loading: false
    };
  },
  computed: {
    ...mapState('search', ['searchKeyword']),
    ...mapGetters('story_board', ['sb_toc']),
    ...mapGetters('report_list', ['current_report_code', 'selected_report']),
    ...mapState('story_board', ['viewer_disclosure_map', 'all_disclosures_loaded']),
    ...mapState('report_list', ['lang']),

    // 검색 페이지용 Cover 정보 (동적)
    searchCoverInfo() {
      return {
        background_color: this.selected_report?.main_color || '#05adc8',
        cover_path: null,
        cover_unsplash_path: '/static_img/examples/search.webp'
      };
    },

    // 검색 페이지용 Cover 제목 정보
    searchCoverLv2Info() {
      return {
        name: '검색 결과',
        name_em: 'Search Result',
        name_ea: 'Search Result'
      };
    }
  },
  watch: {
    searchKeyword() {
      this.fetchResults();
    },
    lang() {
      // 언어 변경 시 검색 결과 다시 가져오기
      if (this.searchKeyword) {
        this.fetchResults();
      }
    }
  },
  created() {
    this.fetchResults();
  },
  methods: {
    ...mapActions('story_board', ['fetch_viewer_sb_disclosures']),

    // [최적화] 전체 데이터를 한 번에 로드 (반영구적 캐싱)
    async loadAllDisclosuresOnce() {
      // [핵심] Vuex store의 all_disclosures_loaded 플래그 확인
      // 이미 로드되어 있으면 즉시 리턴 (SearchResultPage 재진입 시에도 빠름!)
      if (this.all_disclosures_loaded) {
        return;
      }

      this.loading = true;

      try {
        // [핵심] selected_lv_info 없이 호출 → 전체 disclosure 한 번에 로드
        await this.fetch_viewer_sb_disclosures({
          report_code: this.current_report_code
        });
        // store에서 all_disclosures_loaded = true로 설정됨
      } catch (error) {
        console.error('[SearchResultPage] 전체 데이터 로딩 실패:', error);
      } finally {
        this.loading = false;
      }
    },

    // [개선] viewer_disclosure_map 전체를 검색 대상 데이터로 변환
    buildSearchableData() {
      const searchable = [];

      // viewer_disclosure_map의 모든 키를 순회 (동적으로 로드된 데이터 포함)
      for (const lv1Key in this.viewer_disclosure_map) {
        const lv1Data = this.viewer_disclosure_map[lv1Key];

        // lv1 정보 찾기
        const lv1Info = this.sb_toc.find(item => item.key === lv1Key);
        if (!lv1Info) continue;

        for (const lv2Key in lv1Data) {
          const lv2Disclosures = lv1Data[lv2Key];

          // lv2 정보 찾기
          const lv2Info = lv1Info.children?.find(item => item.key === lv2Key);
          if (!lv2Info) continue;

          if (Array.isArray(lv2Disclosures) && lv2Disclosures.length > 0) {
            searchable.push(
              ...lv2Disclosures.filter(item => !item.guide_mode).map(item => ({
                ...item,
                lv1Key: lv1Key,
                lv2Key: lv2Key,
                lv1Name: lv1Info.name,
                lv1NameE: lv1Info.name_em || lv1Info.name_ea,
                lv2Name: lv2Info.name,
                lv2NameE: lv2Info.name_em || lv2Info.name_ea,
                isAppendix: !!lv2Info.appendix_type, // appendix 여부 표시
                appendixType: lv2Info.appendix_type || null
              }))
            );
          }
        }
      }

      return searchable;
    },

    // 검색어 주변 텍스트 추출
    extractContextAroundKeyword(text, keyword, contextLength = 100) {
      if (!text || typeof text !== 'string') return '';

      const cleanText = this.stripCodeFromText(text);
      const lowerText = cleanText.toLowerCase();
      const lowerKeyword = keyword.toLowerCase();
      const keywordIndex = lowerText.indexOf(lowerKeyword);

      if (keywordIndex === -1) {
        return cleanText.substring(0, contextLength * 2);
      }

      const startIndex = Math.max(0, keywordIndex - contextLength);
      const endIndex = Math.min(cleanText.length, keywordIndex + keyword.length + contextLength);
      let excerpt = cleanText.substring(startIndex, endIndex);

      if (startIndex > 0) excerpt = '...' + excerpt;
      if (endIndex < cleanText.length) excerpt = excerpt + '...';

      return excerpt;
    },

    // 셀 값에서 순수 텍스트만 추출
    extractCellText(cell) {
      if (typeof cell === 'object' && cell !== null) {
        const raw = cell.text || cell.value || '';
        return this.stripCodeFromText(String(raw));
      }
      return this.stripCodeFromText(String(cell || ''));
    },

    // 표 데이터를 텍스트로 변환 (검색어 주변만 추출)
    convertTableToText(tableData, keyword) {
      if (!tableData || !Array.isArray(tableData)) return '';

      const lowerKeyword = keyword.toLowerCase();
      const allRows = [];
      const matchedRowIndices = [];

      for (let i = 0; i < tableData.length; i++) {
        const row = tableData[i];
        let rowText = '';

        if (Array.isArray(row)) {
          rowText = row.map(cell => this.extractCellText(cell)).join(' | ');
        } else if (typeof row === 'object' && row !== null) {
          rowText = Object.entries(row)
            .map(([key, value]) => `${key}: ${this.stripCodeFromText(String(value || ''))}`)
            .join(' | ');
        }

        allRows.push(rowText);

        if (rowText.toLowerCase().includes(lowerKeyword)) {
          matchedRowIndices.push(i);
        }
      }

      if (matchedRowIndices.length === 0) {
        return allRows.slice(0, 3).join(' / ');
      }

      const centerIndex = matchedRowIndices[0];
      const startIndex = Math.max(0, centerIndex - 1);
      const endIndex = Math.min(allRows.length, centerIndex + 2);
      const result = allRows.slice(startIndex, endIndex);

      if (startIndex > 0) result[0] = '...' + result[0];
      if (endIndex < allRows.length) result[result.length - 1] = result[result.length - 1] + '...';

      return result.join(' / ');
    },

    // 객체의 여러 키에서 검색어 매칭 시도
    searchInKeys(obj, keys, checkMatch) {
      for (const key of keys) {
        if (checkMatch(obj?.[key])) {
          return obj[key];
        }
      }
      return null;
    },

    // 텍스트에서 HTML 태그, CSS 코드, 속성, CSV 코드를 제거하고 순수 텍스트만 반환
    stripCodeFromText(text) {
      if (!text || typeof text !== 'string') return '';
      let cleaned = text;
      // <style>...</style> 블록 제거
      cleaned = cleaned.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
      // <svg>...</svg> 블록 전체 제거 (path, d 속성 등 포함)
      cleaned = cleaned.replace(/<svg[^>]*>[\s\S]*?<\/svg>/gi, '');
      // URL 제거 (href, src 등에 포함된 링크/경로)
      cleaned = cleaned.replace(/https?:\/\/[^\s"'<>]+/gi, ' ');
      cleaned = cleaned.replace(/\/[\w\-/.]+\.\w{2,4}/g, ' ');
      // HTML 태그 및 속성 제거
      cleaned = cleaned.replace(/<[^>]*>/g, '');
      // CSS 인라인 코드 패턴 제거 (예: color:#fff; font-size:14px;)
      cleaned = cleaned.replace(/[\w-]+\s*:\s*[^;}\n]+[;}/]?/g, match => {
        // CSS 속성 패턴이 아닌 일반 텍스트(한글 포함)는 유지
        if (/[가-힣]/.test(match) || !/^\s*[\w-]+\s*:/.test(match)) return match;
        return ' ';
      });
      // CSV 구분자 패턴 제거 (쉼표로 구분된 숫자/코드 나열)
      cleaned = cleaned.replace(/(\d+,){3,}[\d]*/g, ' ');
      // HTML 엔티티 제거
      cleaned = cleaned.replace(/&[a-zA-Z]+;|&#\d+;/g, ' ');
      // 연속 공백 정리
      cleaned = cleaned.replace(/\s+/g, ' ').trim();
      return cleaned;
    },

    // disclosure에서 검색어 매칭 시도 (언어별 분리)
    findMatchInDisclosure(disclosure, keyword, lowerCaseKeyword) {
      const checkStringMatch = value => {
        if (typeof value !== 'string') return false;
        const cleanedValue = this.stripCodeFromText(value);
        return cleanedValue.toLowerCase().includes(lowerCaseKeyword);
      };

      // 언어별로 검색 필드 분리
      if (this.lang === 'ENG') {
        // 영어 모드: 영어 필드만 검색
        const searchKeysEn = [
          'text_em',
          'text_ea',
          'title1_em',
          'title1_ea',
          'image_title_em',
          'image_title_ea',
          'image_caption_em',
          'image_caption_ea'
        ];
        const dataKeysEn = ['title_e', 'description_e', 'footnote_e'];

        // 1. 최상위 레벨 영어 필드 검색
        let matchedValue = this.searchInKeys(disclosure, searchKeysEn, checkStringMatch);
        if (matchedValue) return matchedValue;

        // 2. data.translated 객체 내부 검색
        if (disclosure.data?.translated) {
          matchedValue = this.searchInKeys(disclosure.data.translated, dataKeysEn, checkStringMatch);
          if (matchedValue) return matchedValue;

          // data.translated.contents_e 검색 (영어 표 데이터)
          if (disclosure.data.translated.contents_e) {
            const tableText = this.convertTableToText(disclosure.data.translated.contents_e, keyword);
            if (this.stripCodeFromText(tableText).toLowerCase().includes(lowerCaseKeyword)) {
              return tableText;
            }
          }
        }

        // 3. documents 배열 영어 검색
        if (Array.isArray(disclosure.documents)) {
          for (const doc of disclosure.documents) {
            if (checkStringMatch(doc.translated?.title_e)) return doc.translated.title_e;
          }
        }
      } else {
        // 한국어 모드: 한국어 필드만 검색
        const searchKeysKo = ['text', 'title1', 'image_title', 'image_caption'];
        const dataKeysKo = ['title', 'description', 'footnote', 'badge_text'];

        // 1. 최상위 레벨 한국어 필드 검색
        let matchedValue = this.searchInKeys(disclosure, searchKeysKo, checkStringMatch);
        if (matchedValue) return matchedValue;

        // 2. data 객체 내부 검색
        if (disclosure.data) {
          matchedValue = this.searchInKeys(disclosure.data, dataKeysKo, checkStringMatch);
          if (matchedValue) return matchedValue;

          // data.contents 검색 (한글 표 데이터)
          if (disclosure.data.contents) {
            const tableText = this.convertTableToText(disclosure.data.contents, keyword);
            if (this.stripCodeFromText(tableText).toLowerCase().includes(lowerCaseKeyword)) {
              return tableText;
            }
          }
        }

        // 3. documents 배열 한국어 검색
        if (Array.isArray(disclosure.documents)) {
          for (const doc of disclosure.documents) {
            if (checkStringMatch(doc.title)) return doc.title;
          }
        }
      }

      return null;
    },

    // 검색 결과 객체 생성
    createSearchResult(disclosure, matchedValue, keyword) {
      const previewText = this.extractContextAroundKeyword(matchedValue, keyword, 100);

      return {
        // [수정] 원본 disclosure의 order_index 사용 (검색 결과 배열 인덱스가 아닌 실제 disclosure 순서)
        order_index: disclosure.order_index,
        lv1_info: {
          key: disclosure.lv1Key,
          name: disclosure.lv1Name,
          name_e: disclosure.lv1NameE
        },
        lv2_info: {
          key: disclosure.lv2Key,
          name: disclosure.lv2Name,
          name_e: disclosure.lv2NameE
        },
        text: previewText,
        text_e: previewText
      };
    },

    // [최적화] 실제 검색 수행 (빠른 검색)
    performSearch(keyword) {
      const lowerCaseKeyword = keyword.toLowerCase();

      // viewer_disclosure_map 전체를 검색 대상으로 변환
      const searchableData = this.buildSearchableData();

      const results = [];

      for (let i = 0; i < searchableData.length; i++) {
        const disclosure = searchableData[i];
        const matchedValue = this.findMatchInDisclosure(disclosure, keyword, lowerCaseKeyword);

        if (matchedValue) {
          // [수정] index 파라미터 제거 - disclosure 자체에 order_index가 있음
          results.push(this.createSearchResult(disclosure, matchedValue, keyword));
        }
      }

      this.searchResults = results;
    },

    // [최적화] 메인 검색 메서드
    async fetchResults() {
      const keyword = this.searchKeyword || '';

      if (!keyword.trim() || !this.sb_toc) {
        this.searchResults = [];
        this.loading = false;
        return;
      }

      // [핵심] 첫 검색: 전체 데이터 한 번에 로드
      // 두 번째 검색부터: 이미 로드된 데이터 사용 (매우 빠름)
      await this.loadAllDisclosuresOnce();

      // 검색 실행 (캐싱된 viewer_disclosure_map 사용)
      this.performSearch(keyword);
    }
  }
};
</script>

<style scoped>
.search-result-page-wrapper {
  min-height: 100vh;
}

.search-content-container {
  min-height: calc(100vh - 50vh);
}
</style>
